import React from "react";
import { useState } from "react";
import { Form, Button, Modal, ModalProps, Drawer, DrawerProps } from "antd";
import { validateFields } from "../../../../../config/Global";
import { assignErrorToInput } from "../../../../../store/api";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import { createRecord } from "../../utils/slice";
import FormComponent from "./FormComponent";
import { useAppDispatch } from "../../../../../utils/hooks/hooks";

interface AddDrawerProps extends DrawerProps {
  titleName: string;
  close: () => void;
}

/**
 * Renders a drawer component for adding a new record to the database for Certifications.
 *
 * @param {AddDrawerProps} props - The props object containing the following properties:
 *   - titleName: The name of the title for the drawer.
 *   - close: A function to close the drawer.
 *   - rest: Any additional props to be spread onto the Modal component.
 * @return {JSX.Element} The rendered drawer component.
 */
const AddComponent: React.FC<AddDrawerProps> = ({
  titleName,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true)
  };

  /**
   * Validates the form fields and updates the disabled state accordingly.
   */
  const validateForm = () => {
    validateFields(form, setDisabled);
  };

  /**
   * Handles the form submission by dispatching create record api with the provided data,
   * and closes the drawer on success or displays error messages on failure.
   *
   * @param {any} data - The form data to be submitted.
   * @return {void} No return value.
   */
  const handleFormSubmit = (data: any) => {
    setSaving(true);
    
    /** remove empty or default values from data object specifically 
         * for text Editor component
         */
    Object.keys(data).forEach((key) => {
      if (data[key] === '<p style="font-size: 14px;"><br data-mce-bogus="1"></p>' ||
        data[key] === '<p style="font-size: 14px;" data-mce-style="font-size: 14px;"><br></p>'
      ) {
        delete data[key]
      }
    })

    dispatch(createRecord(data))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (

    <Drawer
      title={`Add ${titleName}`}
      width="70%"
      onClose={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={
        <>
          <Button
            key="2"
            htmlType="button"
            className="cancelBtn"
            onClick={drawerClose}
          >
            Cancel
          </Button>
          <Button
            key="1"
            disabled={disabled}
            form="addForm"
            loading={saving}
            htmlType="submit"
            type="primary"
          >
            Save
          </Button>
        </>
      }
      {...rest}
    >
      <FormComponent
        form={form}
        id="addForm"
        handleSubmit={handleFormSubmit}
        onValuesChange={validateForm}
      />
    </Drawer>

  );
};

export default AddComponent;
